// src/store/pipeline-views/pipeline-views.store.ts

import { useMemo } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import pipelineViewsService from "@/services/pipeline-views.service";
import {
    TCreatePipelineViewPayload,
    TCreateViewDealPayload,
    TPipelineView,
    TPipelineViewShares,
    TPipelineViewStage,
    TPipelineViewStagePayload,
    TUpdatePipelineViewStagePayload,
    TViewDeal,
} from "@/models/pipeline-views.model";
import { useDealsStore } from "@/store/deals/deals.store";
import { getErrorFromBackend, getErrorStatus } from "@/utils/errors";
import { isSameCalledAt, resolveCalledAt, TCalledAt } from "@/utils/called-at.utils";
import { PipelineViewsState, ViewColumnState } from "./pipeline-views.types";

const DEFAULT_PAGE_SIZE = 20;
const LAST_VIEW_KEY = "pipelineViews:lastViewId";

const createEmptyColumn = (): ViewColumnState => ({
    ids: [],
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalCount: 0,
    loading: false,
    initialized: false,
});

const initialState: PipelineViewsState = {
    listInitialized: false,
    listLoading: false,
    listError: false,
    ids: [],
    entities: {},
    viewStatus: {},
    byView: {},
};

/* ------------------------------------------------------------------ *
 * Last opened view (per browser; storage can be unavailable)
 * ------------------------------------------------------------------ */

export const getLastViewId = (): number | undefined => {
    try {
        const value = Number(localStorage.getItem(LAST_VIEW_KEY));
        return value || undefined;
    } catch {
        return undefined;
    }
};

export const setLastViewId = (viewId: number) => {
    try {
        localStorage.setItem(LAST_VIEW_KEY, String(viewId));
    } catch {
        // ignore — remembering the last view is a convenience
    }
};

/* ------------------------------------------------------------------ *
 * Immutable helpers
 * ------------------------------------------------------------------ */

/** Replace one view column, creating it when asked. */
const withColumn = (
    state: PipelineViewsState,
    viewId: number,
    stageId: number,
    mapColumn: (column: ViewColumnState) => ViewColumnState,
    createIfMissing = false
): PipelineViewsState["byView"] => {
    const view = state.byView[viewId];
    const column = view?.byStage[stageId];
    // Column dropped meanwhile (reset, filters changed, view deleted): keep state as is.
    if (!column && !createIfMissing) return state.byView;

    return {
        ...state.byView,
        [viewId]: {
            byStage: {
                ...(view?.byStage ?? {}),
                [stageId]: mapColumn(column ?? createEmptyColumn()),
            },
        },
    };
};

/** Moves a deal id between two loaded columns (optimistic move and its rollback). */
const moveBetweenColumns = (
    state: PipelineViewsState,
    viewId: number,
    dealId: number,
    fromStageId: number,
    toStageId: number
): Partial<PipelineViewsState> => {
    const byStage = state.byView[viewId]?.byStage;
    const from = byStage?.[fromStageId];
    if (!byStage || !from) return {};
    const to = byStage[toStageId] ?? createEmptyColumn();

    return {
        byView: {
            ...state.byView,
            [viewId]: {
                byStage: {
                    ...byStage,
                    [fromStageId]: {
                        ...from,
                        ids: from.ids.filter((id) => id !== dealId),
                        totalCount: Math.max(0, from.totalCount - 1),
                    },
                    [toStageId]: {
                        ...to,
                        ids: [dealId, ...to.ids.filter((id) => id !== dealId)],
                        totalCount: to.totalCount + 1,
                    },
                },
            },
        },
    };
};

const withView = (state: PipelineViewsState, view: TPipelineView): Partial<PipelineViewsState> => {
    const entities = { ...state.entities, [view.id]: view };
    // keep the list ordered by name, as GET /pipeline-view returns it
    const ids = (state.ids.includes(view.id) ? state.ids : [...state.ids, view.id]).sort((a, b) =>
        (entities[a]?.name ?? "").localeCompare(entities[b]?.name ?? "")
    );
    return { entities, ids, viewStatus: { ...state.viewStatus, [view.id]: "ok" } };
};

const withStages = (
    state: PipelineViewsState,
    viewId: number,
    mapStages: (stages: TPipelineViewStage[]) => TPipelineViewStage[]
): Partial<PipelineViewsState> => {
    const view = state.entities[viewId];
    if (!view) return {};
    const stages = mapStages(view.stages).sort((a, b) => a.order - b.order);
    return { entities: { ...state.entities, [viewId]: { ...view, stages } } };
};

/* ------------------------------------------------------------------ *
 * Store
 * ------------------------------------------------------------------ */

type MoveDealArgs = { viewId: number; dealId: number; fromStageId: number; toStageId: number };

type PipelineViewsActions = {
    resetPipelineViews: () => void;
    fetchViews: (force?: boolean) => Promise<TPipelineView[]>;
    fetchView: (viewId: number) => Promise<TPipelineView | undefined>;
    createView: (payload: TCreatePipelineViewPayload) => Promise<TPipelineView>;
    renameView: (viewId: number, name: string) => Promise<TPipelineView>;
    deleteView: (viewId: number) => Promise<void>;
    saveShares: (viewId: number, shares: TPipelineViewShares) => Promise<TPipelineView>;

    createStage: (viewId: number, payload: TPipelineViewStagePayload) => Promise<TPipelineViewStage>;
    updateStage: (
        viewId: number,
        stageId: number,
        payload: TUpdatePipelineViewStagePayload
    ) => Promise<TPipelineViewStage>;
    deleteStage: (viewId: number, stageId: number) => Promise<void>;
    reorderStages: (viewId: number, orderedStageIds: number[]) => Promise<void>;

    fetchColumn: (args: { viewId: number; stageId: number }) => Promise<void>;
    /** Drops cached columns so the board refetches them (all columns of the view when no ids). */
    resetColumns: (viewId: number, stageIds?: number[]) => void;
    /** Drops every other view's cached columns (a real-stage change may move cards there). */
    resetOtherViews: (viewId: number) => void;
    resetAllColumns: () => void;
    /** Sets the "Called" filter; a change drops every column like the shared filters do. */
    setCalledAt: (calledAt?: TCalledAt) => void;

    moveDeal: (args: MoveDealArgs) => Promise<TViewDeal>;
    createViewDeal: (viewId: number, payload: TCreateViewDealPayload) => Promise<TViewDeal>;
};

export const usePipelineViewsStore = create<PipelineViewsState & PipelineViewsActions>()(
    devtools(
        (set, get) => ({
            ...initialState,

            resetPipelineViews: () => set(initialState, false, "resetPipelineViews"),

            fetchViews: async (force = false) => {
                const { listInitialized, entities, ids } = get();
                if (listInitialized && !force) return ids.map((id) => entities[id]).filter(Boolean);

                set({ listLoading: true, listError: false }, false, "fetchViews/pending");
                try {
                    const views = await pipelineViewsService.getAll();
                    set(
                        (state) => ({
                            listInitialized: true,
                            listLoading: false,
                            listError: false,
                            ids: views.map((v) => v.id),
                            entities: {
                                ...state.entities,
                                ...Object.fromEntries(views.map((v) => [v.id, v])),
                            },
                        }),
                        false,
                        "fetchViews/fulfilled"
                    );
                    return views;
                } catch (error) {
                    // stays uninitialized so the next visit tries again
                    set({ listLoading: false, listError: true }, false, "fetchViews/rejected");
                    throw error;
                }
            },

            fetchView: async (viewId) => {
                set(
                    (state) => ({
                        viewStatus: {
                            ...state.viewStatus,
                            // a refetch keeps showing the loaded view
                            [viewId]: state.entities[viewId] ? state.viewStatus[viewId] ?? "ok" : "loading",
                        },
                    }),
                    false,
                    "fetchView/pending"
                );
                try {
                    const view = await pipelineViewsService.getById(viewId);
                    set((state) => withView(state, view), false, "fetchView/fulfilled");
                    return view;
                } catch (error) {
                    const notFound = getErrorStatus(error) === 404;
                    set(
                        (state) => {
                            const entities = { ...state.entities };
                            if (notFound) delete entities[viewId];
                            return {
                                entities,
                                ids: notFound ? state.ids.filter((id) => id !== viewId) : state.ids,
                                viewStatus: { ...state.viewStatus, [viewId]: notFound ? "notFound" : "error" },
                            };
                        },
                        false,
                        "fetchView/rejected"
                    );
                    return undefined;
                }
            },

            createView: async (payload) => {
                const view = await pipelineViewsService.create(payload);
                set((state) => withView(state, view), false, "createView");
                return view;
            },

            renameView: async (viewId, name) => {
                const view = await pipelineViewsService.rename(viewId, name);
                set((state) => withView(state, view), false, "renameView");
                return view;
            },

            deleteView: async (viewId) => {
                await pipelineViewsService.remove(viewId);
                set(
                    (state) => {
                        const entities = { ...state.entities };
                        delete entities[viewId];
                        const byView = { ...state.byView };
                        delete byView[viewId];
                        return { entities, byView, ids: state.ids.filter((id) => id !== viewId) };
                    },
                    false,
                    "deleteView"
                );
            },

            saveShares: async (viewId, shares) => {
                const view = await pipelineViewsService.share(viewId, shares);
                set((state) => withView(state, view), false, "saveShares");
                return view;
            },

            createStage: async (viewId, payload) => {
                const stage = await pipelineViewsService.createStage(viewId, payload);
                set((state) => withStages(state, viewId, (stages) => [...stages, stage]), false, "createStage");
                return stage;
            },

            updateStage: async (viewId, stageId, payload) => {
                const previous = get().entities[viewId]?.stages.find((s) => s.id === stageId);
                const stage = await pipelineViewsService.updateStage(stageId, payload);
                set(
                    (state) =>
                        withStages(state, viewId, (stages) => stages.map((s) => (s.id === stageId ? stage : s))),
                    false,
                    "updateStage"
                );
                // R8: linking a stage pulls in the deals at that stage that weren't on the view yet.
                if (stage.linkedStatusId && stage.linkedStatusId !== previous?.linkedStatusId) {
                    get().resetColumns(viewId, [stageId]);
                }
                return stage;
            },

            deleteStage: async (viewId, stageId) => {
                await pipelineViewsService.deleteStage(stageId);
                set(
                    (state) => withStages(state, viewId, (stages) => stages.filter((s) => s.id !== stageId)),
                    false,
                    "deleteStage"
                );
                // R10: its cards moved to the last remaining column.
                const stages = get().entities[viewId]?.stages ?? [];
                const last = stages[stages.length - 1];
                get().resetColumns(viewId, last ? [stageId, last.id] : [stageId]);
            },

            reorderStages: async (viewId, orderedStageIds) => {
                const stages = get().entities[viewId]?.stages ?? [];
                const currentIds = stages.map((s) => s.id);
                // `order` values of the slots (1-based on the API); a PATCH moves a stage
                // into a slot and shifts the others, so the slot values never change.
                const slotOrders = stages.map((s) => s.order);
                try {
                    // Walk the new order front to back so each PATCH lands at its final slot.
                    for (let index = 0; index < orderedStageIds.length; index++) {
                        const stageId = orderedStageIds[index];
                        if (currentIds[index] === stageId) continue;
                        await pipelineViewsService.updateStage(stageId, { order: slotOrders[index] });
                        currentIds.splice(currentIds.indexOf(stageId), 1);
                        currentIds.splice(index, 0, stageId);
                    }
                } finally {
                    await get().fetchView(viewId);
                }
            },

            fetchColumn: async ({ viewId, stageId }) => {
                if (!get().byView[viewId]?.byStage[stageId]) {
                    set(
                        (state) => ({ byView: withColumn(state, viewId, stageId, (c) => c, true) }),
                        false,
                        "initViewColumn"
                    );
                }

                const current = get().byView[viewId].byStage[stageId];
                if (current.loading) return;
                if (current.totalPages > 0 && current.page >= current.totalPages) return;

                const nextPage = current.page + 1;
                const { filters } = useDealsStore.getState();
                const { calledAt } = get();

                set(
                    (state) => ({
                        byView: withColumn(state, viewId, stageId, (c) => ({ ...c, loading: true, error: undefined })),
                    }),
                    false,
                    "fetchColumn/pending"
                );
                // A reset while the request is in flight replaces the column object:
                // the late response then belongs to old filters and is dropped.
                const requested = get().byView[viewId]?.byStage[stageId];
                const isCurrent = () => get().byView[viewId]?.byStage[stageId] === requested;

                try {
                    const { data, meta } = await pipelineViewsService.getDeals(
                        viewId,
                        stageId,
                        { ...filters, page: nextPage, pageSize: current.pageSize },
                        calledAt && resolveCalledAt(calledAt)
                    );

                    if (!isCurrent()) return;
                    // Deals live in the shared deals store so the deal sidebar and edits see them.
                    useDealsStore.getState().dealsEntitiesUpserted(data);

                    set(
                        (state) => ({
                            byView: withColumn(state, viewId, stageId, (c) => {
                                const newIds = data.map((deal) => deal.id).filter((id) => !c.ids.includes(id));
                                return {
                                    ...c,
                                    ids: [...c.ids, ...newIds],
                                    page: meta.page,
                                    pageSize: meta.pageSize,
                                    totalPages: meta.totalPages,
                                    totalCount: meta.totalCount,
                                    loading: false,
                                    initialized: true,
                                };
                            }),
                        }),
                        false,
                        "fetchColumn/fulfilled"
                    );
                } catch (error) {
                    if (!isCurrent()) return;
                    set(
                        (state) => ({
                            byView: withColumn(state, viewId, stageId, (c) => ({
                                ...c,
                                loading: false,
                                // stop the column from refetching in a loop
                                initialized: true,
                                error: getErrorFromBackend(error),
                            })),
                        }),
                        false,
                        "fetchColumn/rejected"
                    );
                }
            },

            resetColumns: (viewId, stageIds) =>
                set(
                    (state) => {
                        const view = state.byView[viewId];
                        if (!view) return {};
                        if (!stageIds) {
                            const byView = { ...state.byView };
                            delete byView[viewId];
                            return { byView };
                        }
                        const byStage = { ...view.byStage };
                        stageIds.forEach((id) => delete byStage[id]);
                        return { byView: { ...state.byView, [viewId]: { byStage } } };
                    },
                    false,
                    "resetColumns"
                ),

            resetOtherViews: (viewId) =>
                set(
                    (state) => (state.byView[viewId] ? { byView: { [viewId]: state.byView[viewId] } } : { byView: {} }),
                    false,
                    "resetOtherViews"
                ),

            resetAllColumns: () => set({ byView: {} }, false, "resetAllColumns"),

            setCalledAt: (calledAt) => {
                if (isSameCalledAt(get().calledAt, calledAt)) return;
                set({ calledAt, byView: {} }, false, "setCalledAt");
            },

            moveDeal: async ({ viewId, dealId, fromStageId, toStageId }) => {
                set(
                    (state) => moveBetweenColumns(state, viewId, dealId, fromStageId, toStageId),
                    false,
                    "moveDeal/optimistic"
                );
                try {
                    const deal = await pipelineViewsService.moveDeal(viewId, dealId, toStageId);
                    useDealsStore.getState().dealsEntitiesUpserted([deal]);
                    return deal;
                } catch (error) {
                    set(
                        (state) => moveBetweenColumns(state, viewId, dealId, toStageId, fromStageId),
                        false,
                        "moveDeal/rollback"
                    );
                    throw error;
                }
            },

            createViewDeal: async (viewId, payload) => {
                const deal = await pipelineViewsService.createDeal(viewId, payload);
                useDealsStore.getState().dealsEntitiesUpserted([deal]);
                // The deal now exists on the base pipeline too, possibly on other views (R5).
                const basePipelineId = get().entities[viewId]?.basePipelineId ?? deal.pipelineId;
                useDealsStore.getState().invalidatePipeline(basePipelineId);
                get().resetOtherViews(viewId);
                // R7: the card lands in the column it was added from.
                const stageId = deal.viewStageId ?? payload.viewStageId;
                set(
                    (state) => {
                        const view = state.entities[viewId];
                        return {
                            byView: withColumn(
                                state,
                                viewId,
                                stageId,
                                (c) => {
                                    if (c.ids.includes(deal.id)) return c;
                                    // A column not loaded yet keeps its paging: its first fetch dedupes the id.
                                    return { ...c, ids: [deal.id, ...c.ids], totalCount: c.totalCount + 1 };
                                },
                                true
                            ),
                            // The view chip's count at once; the refetch below settles every view's.
                            ...(view?.dealCount !== undefined && {
                                entities: { ...state.entities, [viewId]: { ...view, dealCount: view.dealCount + 1 } },
                            }),
                        };
                    },
                    false,
                    "createViewDeal"
                );
                // A linked stage can place the deal on other views too, so their counts move as well.
                get().fetchViews(true).catch(() => undefined);
                return deal;
            },
        }),
        { name: "pipelineViews" }
    )
);

// Owner, search and sidebar filters are shared with the Pipelines tab: when they
// change the real board drops its data, and so do the view columns.
useDealsStore.subscribe((state, prev) => {
    if (state.filters !== prev.filters) usePipelineViewsStore.getState().resetAllColumns();
});

/* ------------------------------------------------------------------ *
 * Selector hooks
 * ------------------------------------------------------------------ */

export const useViewColumnDeals = (viewId: number, stageId: number): TViewDeal[] => {
    const ids = usePipelineViewsStore((s) => s.byView[viewId]?.byStage[stageId]?.ids);
    const entities = useDealsStore((s) => s.entities);
    return useMemo(
        () => (ids ?? []).map((id) => entities[id]).filter(Boolean) as TViewDeal[],
        [ids, entities]
    );
};

export const useViewColumnMeta = (viewId: number, stageId: number) => {
    const column = usePipelineViewsStore((s) => s.byView[viewId]?.byStage[stageId]);
    return useMemo(
        () =>
            column && {
                page: column.page,
                pageSize: column.pageSize,
                totalPages: column.totalPages,
                totalCount: column.totalCount,
                loading: column.loading,
                initialized: column.initialized,
                hasMore: column.page < column.totalPages,
                error: column.error,
            },
        [column]
    );
};

export const usePipelineViewsList = (): TPipelineView[] => {
    const ids = usePipelineViewsStore((s) => s.ids);
    const entities = usePipelineViewsStore((s) => s.entities);
    return useMemo(() => ids.map((id) => entities[id]).filter(Boolean), [ids, entities]);
};
